import { BoardingMetadata, DaycareMetadata, status as AppointmentStatus } from '@spike/appointment-model';
import { AppointmentOrderItem, CustomOrderItem, Order, OrderItem, OrderizableTypes } from '@spike/order-model';
import moment, { Moment } from 'moment-timezone';

export const LATE_CHECKOUT_FEE_DESCRIPTION = 'Late PickUp Fee';

const EXCLUDED_APPOINTMENT_STATUSES = [
    AppointmentStatus.CANCELLED,
    AppointmentStatus.NO_SHOW,
    AppointmentStatus.DECLINED
];

type LateCheckoutMetadata = DaycareMetadata | BoardingMetadata;

export const lateCheckoutAmountsMatch = (left: number, right: number): boolean => {
    return Math.round(left * 100) === Math.round(right * 100);
};

const roundMoney = (value: number): number => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
};

const readMetadata = (
    appointmentItem: AppointmentOrderItem
): { businessAreaId?: string; metadata?: LateCheckoutMetadata } => {
    const businessAreaId = appointmentItem.appointment.services[0]?.businessArea?.id;
    const metadata = appointmentItem.appointment.serviceVariableDuration as LateCheckoutMetadata | undefined;
    return { businessAreaId, metadata };
};

const feeForPet = (fees: Record<string, number> | undefined, petType?: { id?: string; plural?: string }): number => {
    if (!fees) return 0;
    const id = petType?.id?.toLowerCase();
    const plural = petType?.plural?.toLowerCase();
    const raw = (id ? fees[id] : undefined) ?? (plural ? fees[plural] : undefined) ?? 0;
    return Number(raw) || 0;
};

const periodHoursFromDuration = (durationValue?: string): number => {
    if (!durationValue) return 0;
    const match = durationValue.match(/(?<!\d)(?:[2-9]|1[0-2])(?!\d)/);
    return match ? Number(match[0]) : 0;
};

const hoursAfterDeadline = (deadline: Moment, now: Moment): number => {
    const diffMs = now.diff(deadline);
    if (diffMs <= 0) return 0;
    return Math.ceil(moment.duration(diffMs).asHours());
};

/**
 * Fee for one appointment.
 * Automatic charges require the service-level Late Checkout Fee to be enabled.
 * The booking-level control can still preview a calculated amount when it is off.
 */
export const calculateAppointmentLateCheckoutFee = (
    appointmentItem: AppointmentOrderItem,
    timeZone: string,
    options?: { requireEnabled?: boolean; now?: Moment }
): number => {
    if (EXCLUDED_APPOINTMENT_STATUSES.includes(appointmentItem.appointment.status?.id)) {
        return 0;
    }

    const { businessAreaId, metadata } = readMetadata(appointmentItem);
    if (businessAreaId !== 'daycare' && businessAreaId !== 'boarding') return 0;

    const latePickupFee = metadata?.latePickupFee;
    if (!latePickupFee) return 0;
    if (options?.requireEnabled && !latePickupFee.enabled) return 0;

    const appointmentEnd = appointmentItem.appointment.duration?.to;
    if (!appointmentEnd) return 0;

    const now = options?.now ?? moment.tz(timeZone);
    const deadline = appointmentEnd
        .clone()
        .tz(timeZone)
        .add(latePickupFee.gracePeriod || 0, 'minutes');
    if (!now.isAfter(deadline)) return 0;

    const price = feeForPet(latePickupFee.fees, appointmentItem.appointment.pet?.type);
    if (!price) return 0;

    const lateHours = hoursAfterDeadline(deadline, now);

    if (businessAreaId === 'daycare') {
        switch (latePickupFee.type) {
            case 'per_hour':
                return price * lateHours;
            case 'additional_half_day':
            case 'additional_full_day': {
                const periodHours = periodHoursFromDuration((metadata as DaycareMetadata).durationValue);
                if (!periodHours) return 0;
                const elapsedHours = moment.duration(now.diff(deadline)).asHours();
                return Math.ceil(elapsedHours / periodHours) * price;
            }
            default:
                return 0;
        }
    }

    switch (latePickupFee.type) {
        case 'per_hour':
            return price * lateHours;
        case 'additional_night':
        case 'fixed_fee':
            return price;
        default:
            return 0;
    }
};

export const calculateLateCheckoutFee = (
    appointments: Array<AppointmentOrderItem>,
    timeZone: string,
    options?: { requireEnabled?: boolean; now?: Moment }
): number => {
    const total = appointments.reduce(
        (sum, appointment) => sum + calculateAppointmentLateCheckoutFee(appointment, timeZone, options),
        0
    );
    return roundMoney(total);
};

export const calculateAutomaticLateCheckoutFee = (
    appointments: Array<AppointmentOrderItem>,
    timeZone: string,
    now?: Moment
): number => {
    return calculateLateCheckoutFee(appointments, timeZone, { requireEnabled: true, now });
};

export const findLateCheckoutFeeCustomItem = (order?: Order): CustomOrderItem | undefined => {
    return order?.items.customs?.find(custom => custom.orderItem.description === LATE_CHECKOUT_FEE_DESCRIPTION);
};

export const buildLateCheckoutFeeOrderItem = (params: {
    order: Order;
    amount: number;
    timeZone: string;
    uuid: string;
    existing?: CustomOrderItem;
}): OrderItem => {
    const existingItem = params.existing?.orderItem;
    return {
        id: existingItem?.id,
        uuid: existingItem?.uuid ?? params.uuid,
        orderId: params.order.id,
        orderizable: {
            type: OrderizableTypes.CUSTOM
        },
        petId: existingItem?.petId ?? params.order.items.appointments[0]?.appointment?.pet?.id,
        staffId: existingItem?.staffId,
        description: LATE_CHECKOUT_FEE_DESCRIPTION,
        quantity: 1,
        amount: roundMoney(params.amount).toString(),
        discount: existingItem?.discount ?? { amount: '0' },
        startedAt: existingItem?.startedAt ?? moment.tz(params.timeZone)
    };
};
